import React, { useEffect, useRef, useState } from 'react';

// Count-up stat that resets to 0 and re-animates every time it enters the viewport.
// Non-numeric values (e.g. "24/7") render as-is without animating.
const CountUp = ({ value, duration = 2000, className = '' }) => {
  const str = String(value).trim();
  const match = /^(\d+(?:\.\d+)?)(\+|%|M\+|K\+|x)?$/.exec(str);
  const end = match ? parseFloat(match[1]) : null;
  const suffix = match ? (match[2] || '') : '';
  const [count, setCount] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    if (end === null) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setCount(end);
      return;
    }
    const node = ref.current;
    let raf = null;
    const run = () => {
      let startTime;
      const step = (t) => {
        if (!startTime) startTime = t;
        const progress = Math.min((t - startTime) / duration, 1);
        setCount(Math.floor(progress * end));
        if (progress < 1) raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);
    };
    const observer = new IntersectionObserver(([entry]) => {
      if (raf !== null) cancelAnimationFrame(raf);
      setCount(0);
      if (entry.isIntersecting) run();
    }, { threshold: 0.3 });
    observer.observe(node);
    return () => {
      observer.disconnect();
      if (raf !== null) cancelAnimationFrame(raf);
    };
  }, [end, duration]);

  return (
    <span ref={ref} className={className} data-testid="stat-counter">
      {end === null ? str : `${count}${suffix}`}
    </span>
  );
};

export default CountUp;
